/**
 * ProjectRepositorySettings
 * GitHub repo picker (when connected) or manual URL inputs (when disconnected).
 * Branch picker via GitHub API, optional "Create New Repo" form.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Github, Search, ChevronDown, Plus, ExternalLink, GitBranch, Lock, Globe } from 'lucide-react';
import { useProject } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { projectsApi } from '../../api/projects';
import {
  useGitHubStatus,
  useGitHubRepos,
  useGitHubBranches,
  useCreateGitHubRepo,
  type GitHubRepo,
} from '../../api/workspace-github';
import { Panel, PanelHeader, PanelBody } from '../shared/Panel';

function parseOwnerRepo(url: string): { owner: string; repo: string } | null {
  const match = url.match(/github\.com[/:]([^/]+)\/([^/.]+)/);
  if (match) return { owner: match[1], repo: match[2] };
  return null;
}

export function ProjectRepositorySettings() {
  const { project, refreshProjects } = useProject();
  const { currentWorkspace } = useWorkspace();
  const workspaceId = currentWorkspace?.id;

  const { data: ghStatus } = useGitHubStatus(workspaceId);
  const isGitHubConnected = ghStatus?.connected ?? false;

  const [repoUrl, setRepoUrl] = useState('');
  const [repoBranch, setRepoBranch] = useState('main');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Repo picker state
  const [showRepoPicker, setShowRepoPicker] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [repoPage, setRepoPage] = useState(1);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Create new repo state
  const [showCreateRepo, setShowCreateRepo] = useState(false);
  const [newRepoName, setNewRepoName] = useState('');
  const [newRepoDesc, setNewRepoDesc] = useState('');
  const [newRepoPrivate, setNewRepoPrivate] = useState(true);

  const { data: reposData, isLoading: loadingRepos } = useGitHubRepos(
    isGitHubConnected ? workspaceId : undefined,
    { page: repoPage, search: repoSearch }
  );

  const parsed = parseOwnerRepo(repoUrl);
  const { data: branchesData } = useGitHubBranches(
    isGitHubConnected ? workspaceId : undefined,
    parsed?.owner ?? '',
    parsed?.repo ?? ''
  );

  const createRepoMutation = useCreateGitHubRepo();

  useEffect(() => {
    if (project) {
      setRepoUrl(project.repository?.url || '');
      setRepoBranch(project.repository?.branch || 'main');
    }
  }, [project]);

  // Close picker on click outside
  useEffect(() => {
    if (!showRepoPicker) return;
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowRepoPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showRepoPicker]);

  const handleSelectRepo = useCallback((repo: GitHubRepo) => {
    setRepoUrl(repo.html_url);
    setRepoBranch(repo.default_branch);
    setShowRepoPicker(false);
  }, []);

  const handleCreateRepo = useCallback(async () => {
    if (!workspaceId || !newRepoName.trim()) return;
    try {
      const repo = await createRepoMutation.mutateAsync({
        workspaceId,
        name: newRepoName.trim(),
        description: newRepoDesc.trim() || undefined,
        isPrivate: newRepoPrivate,
      });
      setRepoUrl(repo.html_url);
      setRepoBranch(repo.default_branch);
      setShowCreateRepo(false);
      setShowRepoPicker(false);
      setNewRepoName('');
      setNewRepoDesc('');
    } catch (err) {
      console.error('Failed to create repo:', err);
    }
  }, [workspaceId, newRepoName, newRepoDesc, newRepoPrivate, createRepoMutation]);

  const handleSave = useCallback(async () => {
    if (!project) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      await projectsApi.update(project.id, {
        repository: { url: repoUrl.trim(), branch: repoBranch.trim() || 'main' },
      });
      await refreshProjects();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update repository:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to update repository');
    } finally {
      setIsSaving(false);
    }
  }, [project, repoUrl, repoBranch, refreshProjects]);

  if (!project) {
    return (
      <Panel>
        <PanelBody>
          <div className="text-center py-8 text-muted text-sm">
            Select a project to configure its repository.
          </div>
        </PanelBody>
      </Panel>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection status banner */}
      {isGitHubConnected ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-[12px]">
          <Github size={14} />
          Connected to <span className="font-semibold">{ghStatus?.orgLogin}</span> via GitHub App
        </div>
      ) : (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-surface-muted border border-line text-muted text-[12px]">
          <span className="flex items-center gap-2">
            <Github size={14} className="text-subtle" />
            GitHub not connected. Configure in workspace settings for repo picker.
          </span>
          <a href="/settings/github" className="text-coral-500 hover:text-coral-600 font-medium flex items-center gap-1">
            Connect <ExternalLink size={10} />
          </a>
        </div>
      )}

      <Panel>
        <PanelHeader title="Repository" />
        <PanelBody>
          <div className="space-y-4">
            {saveSuccess && (
              <div className="p-2.5 bg-green-50 border border-green-200 rounded-lg text-green-800 text-[12px]">
                Repository settings saved.
              </div>
            )}
            {saveError && (
              <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-800 text-[12px]">
                {saveError}
              </div>
            )}

            {/* Repo URL + Picker */}
            <div>
              <label className="block text-[12px] font-medium text-text mb-1">
                Repository URL
              </label>
              <div className="relative" ref={pickerRef}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                    className="flex-1 px-3 py-2 border border-line rounded-lg text-sm font-mono focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none transition-colors"
                    placeholder="https://github.com/org/repo"
                  />
                  {isGitHubConnected && (
                    <button
                      type="button"
                      onClick={() => setShowRepoPicker(!showRepoPicker)}
                      className="px-3 py-2 border border-line rounded-lg text-sm text-muted hover:bg-surface-muted transition-colors flex items-center gap-1.5"
                    >
                      <Search size={14} />
                      Browse
                      <ChevronDown size={12} className={`transition-transform ${showRepoPicker ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>

                {/* Repo Picker Dropdown */}
                {showRepoPicker && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-line rounded-lg shadow-lg z-20 max-h-[360px] overflow-hidden flex flex-col">
                    {/* Search */}
                    <div className="p-2 border-b border-line">
                      <div className="relative">
                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-subtle" />
                        <input
                          type="text"
                          value={repoSearch}
                          onChange={(e) => { setRepoSearch(e.target.value); setRepoPage(1); }}
                          className="w-full pl-8 pr-3 py-1.5 border border-line rounded text-[12px] focus:ring-1 focus:ring-coral-400 outline-none"
                          placeholder="Search repositories..."
                          autoFocus
                        />
                      </div>
                    </div>

                    {/* Repo list */}
                    <div className="overflow-y-auto flex-1 max-h-[240px]">
                      {loadingRepos ? (
                        <div className="px-3 py-6 text-center text-subtle text-[12px]">
                          <div className="animate-spin inline-block w-4 h-4 border-2 border-line border-t-coral-400 rounded-full mb-2" />
                          <div>Loading repos...</div>
                        </div>
                      ) : reposData?.repos?.length ? (
                        reposData.repos.map(repo => (
                          <button
                            key={repo.id}
                            type="button"
                            onClick={() => handleSelectRepo(repo)}
                            className="w-full text-left px-3 py-2 hover:bg-surface-muted transition-colors border-b border-neutral-50 last:border-b-0"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[12px] font-medium text-text">{repo.name}</span>
                              {repo.private ? (
                                <Lock size={10} className="text-subtle" />
                              ) : (
                                <Globe size={10} className="text-subtle" />
                              )}
                              {repo.language && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-surface-subtle text-muted rounded-full">{repo.language}</span>
                              )}
                            </div>
                            {repo.description && (
                              <p className="text-[11px] text-subtle mt-0.5 truncate">{repo.description}</p>
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-6 text-center text-subtle text-[12px]">
                          No repositories found
                        </div>
                      )}
                    </div>

                    {/* Pagination */}
                    {reposData && reposData.totalCount > 30 && (
                      <div className="flex items-center justify-between px-3 py-1.5 border-t border-line text-[11px] text-subtle">
                        <span>{reposData.totalCount} repos</span>
                        <div className="flex gap-1">
                          <button
                            disabled={repoPage <= 1}
                            onClick={() => setRepoPage(p => Math.max(1, p - 1))}
                            className="px-2 py-0.5 rounded hover:bg-surface-subtle disabled:opacity-30"
                          >
                            Prev
                          </button>
                          <button
                            onClick={() => setRepoPage(p => p + 1)}
                            className="px-2 py-0.5 rounded hover:bg-surface-subtle"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Create new repo toggle */}
                    <div className="border-t border-line">
                      <button
                        type="button"
                        onClick={() => setShowCreateRepo(!showCreateRepo)}
                        className="w-full px-3 py-2 text-[12px] text-coral-600 hover:bg-coral-50/50 transition-colors flex items-center gap-1.5 font-medium"
                      >
                        <Plus size={12} />
                        Create New Repository
                      </button>
                      {showCreateRepo && (
                        <div className="px-3 pb-3 space-y-2">
                          <input
                            type="text"
                            value={newRepoName}
                            onChange={(e) => setNewRepoName(e.target.value)}
                            placeholder="repo-name"
                            className="w-full px-2.5 py-1.5 border border-line rounded text-[12px] font-mono focus:ring-1 focus:ring-coral-400 outline-none"
                          />
                          <input
                            type="text"
                            value={newRepoDesc}
                            onChange={(e) => setNewRepoDesc(e.target.value)}
                            placeholder="Description (optional)"
                            className="w-full px-2.5 py-1.5 border border-line rounded text-[12px] focus:ring-1 focus:ring-coral-400 outline-none"
                          />
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-1.5 text-[11px] text-muted cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newRepoPrivate}
                                onChange={(e) => setNewRepoPrivate(e.target.checked)}
                                className="rounded border-line-strong text-coral-500 focus:ring-coral-400"
                              />
                              Private
                            </label>
                            <button
                              type="button"
                              onClick={handleCreateRepo}
                              disabled={!newRepoName.trim() || createRepoMutation.isPending}
                              className="px-3 py-1 bg-coral-500 text-white text-[12px] rounded hover:bg-coral-600 disabled:opacity-50 transition-colors"
                            >
                              {createRepoMutation.isPending ? 'Creating...' : 'Create'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Create New Repository — always visible when GitHub connected */}
            {isGitHubConnected && (
              <div className="border border-line rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowCreateRepo(!showCreateRepo)}
                  className="w-full px-4 py-3 text-[13px] text-text hover:bg-surface-muted transition-colors flex items-center gap-2 font-medium"
                >
                  <Plus size={14} className="text-coral-500" />
                  Create New Repository
                  <ChevronDown size={12} className={`ml-auto text-subtle transition-transform ${showCreateRepo ? 'rotate-180' : ''}`} />
                </button>
                {showCreateRepo && (
                  <div className="px-4 pb-4 pt-1 border-t border-line space-y-3">
                    <div>
                      <label className="block text-[11px] font-medium text-muted mb-1">Repository Name</label>
                      <input
                        type="text"
                        value={newRepoName}
                        onChange={(e) => setNewRepoName(e.target.value)}
                        placeholder="my-project"
                        className="w-full px-3 py-2 border border-line rounded-lg text-sm font-mono focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-muted mb-1">Description <span className="text-subtle">(optional)</span></label>
                      <input
                        type="text"
                        value={newRepoDesc}
                        onChange={(e) => setNewRepoDesc(e.target.value)}
                        placeholder="A short description"
                        className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none transition-colors"
                      />
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <label className="flex items-center gap-2 text-[12px] text-muted cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newRepoPrivate}
                          onChange={(e) => setNewRepoPrivate(e.target.checked)}
                          className="rounded border-line-strong text-coral-500 focus:ring-coral-400"
                        />
                        Private repository
                      </label>
                      <button
                        type="button"
                        onClick={handleCreateRepo}
                        disabled={!newRepoName.trim() || createRepoMutation.isPending}
                        className="px-4 py-2 bg-coral-500 text-white text-[12px] font-medium rounded-lg hover:bg-coral-600 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                      >
                        {createRepoMutation.isPending ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Github size={12} />
                            Create on GitHub
                          </>
                        )}
                      </button>
                    </div>
                    {createRepoMutation.isError && (
                      <div className="p-2 bg-red-50 border border-red-200 rounded text-red-700 text-[11px]">
                        {createRepoMutation.error instanceof Error ? createRepoMutation.error.message : 'Failed to create repository'}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Branch */}
            <div>
              <label className="block text-[12px] font-medium text-text mb-1">
                <GitBranch size={12} className="inline mr-1" />
                Branch
              </label>
              {isGitHubConnected && branchesData?.branches?.length ? (
                <select
                  value={repoBranch}
                  onChange={(e) => setRepoBranch(e.target.value)}
                  className="w-full px-3 py-2 border border-line rounded-lg text-sm focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none bg-white transition-colors"
                >
                  {branchesData.branches.map(b => (
                    <option key={b.name} value={b.name}>
                      {b.name}{b.protected ? ' (protected)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={repoBranch}
                  onChange={(e) => setRepoBranch(e.target.value)}
                  className="w-full px-3 py-2 border border-line rounded-lg text-sm font-mono focus:ring-1 focus:ring-coral-500/30 focus:border-coral-400 outline-none transition-colors"
                  placeholder="main"
                />
              )}
              <p className="mt-1 text-[11px] text-subtle">
                Used by Sprites when cloning the project for builds.
              </p>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-2 bg-coral-500 text-white text-sm font-medium rounded-lg hover:bg-coral-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                    Saving...
                  </>
                ) : (
                  'Save Repository'
                )}
              </button>
            </div>
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
}
